import type { ForwardRefRenderFunction, KeyboardEventHandler } from 'react';
import React, {
  useImperativeHandle,
  forwardRef,
  useRef,
  useCallback,
} from 'react';
import classNames from 'clsx';

import {
  activateByEnterButton,
  formatClassNames,
} from '@wix/editor-elements-common-utils';
import type {
  ILoginSocialBarCustomMenu,
  ILoginSocialBarProps,
  LoginSocialBarMenuItemProps,
} from '../../../../LoginSocialBar.types';
import { TEST_ID_CUSTOM_MENU, TEST_ID_LINK } from '../../../../constants';
import {
  BIAction,
  getMembersAreaNavigationEvent,
  MEMBERS_APP_UOU_ENDPOINT,
} from '../../../bi/events';
import styles from './LoginSocialBarCustomMenu.module.scss';
import { createMenuKeyboardNavigationHandler } from '../../../../utils/keyboardUtils';
import semanticClassNames from '../../LoginSocialBar.semanticClassNames';
import { useMenuPositioning } from './hooks';
import { MenuItems } from './MenuItems';

type ILoginSocialBarCustomMenuProps = Pick<
  ILoginSocialBarProps,
  | 'menuItems'
  | 'onLogout'
  | 'translations'
  | 'currentPrimaryPageHref'
  | 'reportBi'
> & {
  ariaLabel: string;
  open: boolean;
  onClose(): void;
  reportBiOnMenuItemClick: (item: LoginSocialBarMenuItemProps) => void;
  rootElement: HTMLDivElement | null;
  referenceElement: HTMLDivElement | null;
  useNestedMenuItems: boolean;
  withNativeThemeOverrides: boolean;
  withNewColorWiring: boolean;
  useNewDropdownMenuAlignment: boolean;
};

export const LOGIN_SOCIAL_BAR_CUSTOM_MENU_ID = 'loginSocialBarCustomMenu';

const queryFocusableMenuElements = (menuElement: HTMLElement) =>
  Array.from(menuElement.querySelectorAll<HTMLElement>('[tabindex],[href]'));

const LoginSocialBarCustomMenu: ForwardRefRenderFunction<
  ILoginSocialBarCustomMenu,
  ILoginSocialBarCustomMenuProps
> = (
  {
    ariaLabel,
    menuItems,
    translations,
    currentPrimaryPageHref,
    open,
    rootElement,
    referenceElement,
    useNestedMenuItems,
    onLogout,
    onClose,
    reportBi,
    reportBiOnMenuItemClick,
    withNativeThemeOverrides,
    withNewColorWiring,
    useNewDropdownMenuAlignment,
  },
  ref,
) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { menuStyles, isVisible } = useMenuPositioning({
    isMenuOpen: open,
    rootElement,
    referenceElement,
    popperElement: containerRef.current,
    useNewDropdownMenuAlignment,
  });

  const className = classNames(styles.root, {
    [styles.open]: open,
    [styles.visible]: isVisible,
  });

  useImperativeHandle(ref, () => ({
    focusFirstMenuItem: () => {
      if (!containerRef.current) {
        return;
      }
      const [firstElement] = queryFocusableMenuElements(containerRef.current);
      if (firstElement) {
        firstElement.focus();
      }
    },
  }));

  const handleKeyDown: KeyboardEventHandler =
    createMenuKeyboardNavigationHandler(onClose, containerRef);

  const onLinkClick = useCallback(
    (item: LoginSocialBarMenuItemProps) => {
      reportBiOnMenuItemClick(item);
      void reportBi(
        getMembersAreaNavigationEvent({
          action: BIAction.ItemClick,
          pageName: item.label,
        }),
        {
          endpoint: MEMBERS_APP_UOU_ENDPOINT,
        },
      );
    },
    [reportBiOnMenuItemClick, reportBi],
  );

  const handleLogout = () => {
    reportBiOnMenuItemClick({ label: translations.logOutText!, link: {} });
    onLogout();
  };

  return (
    <div
      id={LOGIN_SOCIAL_BAR_CUSTOM_MENU_ID}
      ref={containerRef}
      className={classNames(
        formatClassNames(semanticClassNames.dropdownMenu),
        semanticClassNames.dropdownMenu,
        className,
        {
          [styles.withNativeThemeOverrides]: withNativeThemeOverrides,
          [styles.withNewColorWiring]: withNewColorWiring,
          [styles.inheritAlignment]: useNewDropdownMenuAlignment,
        },
      )}
      aria-label={ariaLabel}
      role="menu"
      data-testid={TEST_ID_CUSTOM_MENU}
      onKeyDown={handleKeyDown}
      onClick={onClose}
      style={menuStyles}
    >
      <MenuItems
        items={menuItems}
        onLinkClick={onLinkClick}
        currentPrimaryPageHref={currentPrimaryPageHref}
        useNestedMenuItems={useNestedMenuItems}
      />

      <ul>
        {!!menuItems.length && (
          <li role="separator">
            <hr />
          </li>
        )}
        <li>
          <div
            role="menuitem"
            className={styles.link}
            onClick={handleLogout}
            tabIndex={0}
            data-testid={TEST_ID_LINK}
            onKeyDown={activateByEnterButton}
          >
            <span className={styles.label}>{translations.logOutText}</span>
          </div>
        </li>
      </ul>
    </div>
  );
};

export default forwardRef(LoginSocialBarCustomMenu);
